import * as React from 'react';
import 'font-awesome/css/font-awesome.min.css';
import "primereact/resources/themes/fluent-light/theme.css";
import "primeflex/primeflex.css"
import "primereact/resources/primereact.css";
import "primeicons/primeicons.css";
import './../public/assets/css/App.css'
import { ProgramContainer } from './entities/formTemplateDataType';
import { User } from './entities/User';
import { WelcomeComponent } from './commonComponent/WelcomeComponent';
import { HeaderComponent } from './commonComponent/HeaderComponent';
import { FooterComponent } from './commonComponent/FooterComponent';
import { ListFormComponent } from './designComponent/ListFormComponent';
import { DetailFormComponent } from './designComponent/DetailFormComponent';
import './../public/assets/css/ontario-design-system/styles/ds-theme.min.css'
import 'regenerator-runtime/runtime'
import LocalStorageService from './services/SessionStorageService';
import msalInstance from "./security/msalInstance";
import { MsalProvider } from '@azure/msal-react';
import { parse } from 'flatted';
import * as CircularJSON from 'circular-json';
import SectionUtils from "./utilities/SectionUtils";

interface Props {

}

interface State {
    user: User,
    isAuthenticated: boolean,
    hideWelcome: boolean;
    hideDetail: boolean;
    hideList: boolean;
    programContainer: ProgramContainer;
    graphData,
    isFormDirty: boolean;
    entityVersion: number;
}

export class ContainerForm extends React.Component<Props, State> {
    hash: string;
    timer;
    private initialFormData: string = "";
    private initialFormValues: any = null;
    private entityObserverAttachedTo?: ProgramContainer;
    private hasMounted = false;
    private strProgramContainer: string = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            user: null,
            hideWelcome: false,
            hideList: true,
            hideDetail: true,
            programContainer: null,
            isAuthenticated: false,
            graphData: null,
            isFormDirty: false,
            entityVersion: 0,
        };

        this.timer = null;
    }

    // Extract only user input values from form sections, excluding navigation/visibility state and buttons
    extractFormValues = (programContainer) => {
        if (!programContainer?.ListOfFormSections) {
            return {};
        }

        const values = {};

        // Element types that should NOT trigger dirty check
        const excludedTypes = [
            'label',
            'html',
            'button',
            'submit',
            'reset',
            'navigation',
            'stepper',
            'link'
        ];

        const extractElementValues = (elements, path = '') => {
            if (!elements || !Array.isArray(elements)) {
                return;
            }

            elements.forEach((element) => {
                const elementType = (element.Datatype || element.Type || '').toLowerCase();
                const elementName = (element.Name || '').toLowerCase();

                // Skip excluded types
                if (elementType && excludedTypes.includes(elementType)) {
                    return;
                }

                // Skip elements with "button" in their name or type
                if (elementName.includes('button') || elementType.includes('button')) {
                    return;
                }

                // Skip title/heading elements
                if (elementName.includes('title') || elementName.includes('heading') ||
                    elementType.includes('title') || elementType.includes('heading')) {
                    return;
                }

                // Track all elements that have a LoadBindName (binding field name)
                if (element.LoadBindName) {
                    const key = element.LoadBindName;
                    values[key] = {
                        Value: element.Value,
                        fieldValue: element.fieldValue,
                        day: element.day,
                        month: element.month,
                        year: element.year
                    };
                }

                // Recursively check subsections
                if (element.ListOfFormSubSection) {
                    element.ListOfFormSubSection.forEach((subSection) => {
                        if (subSection.ListOfFormSubSectionEle) {
                            extractElementValues(subSection.ListOfFormSubSectionEle, `${path}.${element.Id}`);
                        }
                    });
                }
            });
        };

        programContainer.ListOfFormSections.forEach((section) => {
            // Check for direct elements on section
            if (section.ListOfFormSectionElements && Array.isArray(section.ListOfFormSectionElements)) {
                extractElementValues(section.ListOfFormSectionElements, section.Id);
            }

            // Also check subsections
            if (section.ListOfFormSubSections && Array.isArray(section.ListOfFormSubSections)) {
                section.ListOfFormSubSections.forEach((subSection) => {
                    if (subSection.ListOfFormSectionElements) {
                        extractElementValues(subSection.ListOfFormSectionElements, section.Id);
                    }
                });
            }
        });

        return values;
    };

    // Capture initial form data for dirty checking
    captureInitialFormData = () => {
        try {
            if (this.state.programContainer) {
                // Only compare actual user input values, exclude navigation/visibility state
                const formValues = this.extractFormValues(this.state.programContainer);
                this.initialFormValues = formValues;
                // Use JSON.stringify since we're only comparing simple values now
                this.initialFormData = JSON.stringify(formValues);

                // Reset IsDirty flags on all fields based on the current baseline
                SectionUtils.applyDirtyFlags(
                    this.state.programContainer,
                    this.initialFormValues,
                    formValues,
                );
            }
        } catch (error) {
            console.error("Error capturing initial form data:", error);
        }
    };

    // Check if form is dirty by comparing current state with initial state
    checkIfFormIsDirty = (): boolean => {
        try {
            if (!this.initialFormData || !this.state.programContainer) {
                return false;
            }
            // Only compare actual user input values, exclude navigation/visibility state
            const currentValues = this.extractFormValues(this.state.programContainer);

            // Update IsDirty flags on individual fields
            if (this.initialFormValues) {
                SectionUtils.applyDirtyFlags(
                    this.state.programContainer,
                    this.initialFormValues,
                    currentValues,
                );
            }

            const currentFormData = JSON.stringify(currentValues);
            const isDirty = currentFormData !== this.initialFormData;
            return isDirty;
        } catch (error) {
            console.error("Error checking if form is dirty:", error);
            return false;
        }
    };

    // Mark form as dirty
    markFormAsDirty = () => {
        if (!this.state.isFormDirty) {
            this.setState({ isFormDirty: true });
        }
    };

    // Clear dirty flag (e.g., after successful save/submit)
    clearDirtyFlag = () => {
        if (this.state.programContainer) {
            SectionUtils.resetDirtyFlags(this.state.programContainer);
        }
        this.captureInitialFormData();
        this.setState({ isFormDirty: false });
    };

    // Handle browser beforeunload event to warn about unsaved changes
    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.state.isFormDirty) {
            e.preventDefault();
            e.returnValue = ''; // Required for Chrome
        }
    };

    private attachEntityChangeObserver(programContainer?: ProgramContainer | null) {
        if (!programContainer) {
            return;
        }

        if (this.entityObserverAttachedTo === programContainer) {
            return;
        }

        const descriptor = Object.getOwnPropertyDescriptor(programContainer, "Entity");
        if (descriptor?.get || descriptor?.set) {
            this.entityObserverAttachedTo = programContainer;
            return;
        }

        let currentEntity = programContainer.Entity;
        Object.defineProperty(programContainer, "Entity", {
            configurable: true,
            enumerable: true,
            get: () => currentEntity,
            set: (next) => {
                if (next === currentEntity) return;
                currentEntity = next;
                if (this.hasMounted) {
                    this.setState(prev => ({
                        entityVersion: (prev.entityVersion ?? 0) + 1,
                    }));
                }
            },
        });

        this.entityObserverAttachedTo = programContainer;
    }

    componentDidMount(): void {
        this.hasMounted = true;
        this.attachEntityChangeObserver(this.state.programContainer);
        if (sessionStorage.getItem('stepperIndex')) {
            sessionStorage.removeItem('stepperIndex');
            sessionStorage.removeItem('activeStepList');
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if (this.state.programContainer !== prevState.programContainer) {
            this.attachEntityChangeObserver(this.state.programContainer);
        }
        // Capture initial form data when programContainer is first loaded or changed
        if (this.state.programContainer !== prevState.programContainer &&
            this.state.programContainer &&
            !this.state.hideDetail) {

            // Reset dirty flag when loading a new form
            this.setState({ isFormDirty: false }, () => {
                // Capture immediately after state is set
                this.captureInitialFormData();
            });
        }
    }

    componentWillUnmount(): void {
        this.hasMounted = false;
    }

    render() {
       
        return (
            <MsalProvider instance={msalInstance}>
            <div style={{ height: '83px', maxWidth: '100%', marginBottom: '30px' }} >
                <div style={{ marginBottom: '30px' }} >
                    <HeaderComponent
                        callback={() => { this.setState({ hideWelcome: true, hideDetail: true,  hideList: false})}}
                        userLoadedCallBack={()=>{

                        }}
                        programContainer={this.state.programContainer}
                        isFormDirty={this.state.isFormDirty}
                        showHelpLink={!this.state.hideWelcome || !this.state.hideList || !this.state.hideDetail}
                    />
                </div>
                <div>
                    <WelcomeComponent state={this.state}
                        callback={(p) => { let user = this.state.user; this.setState({ hideWelcome: true, hideDetail: true, hideList: false, user: user }, () => window.scrollTo(0, 0)); }} />
                </div>

                <div style={{ maxWidth: '80%', margin: '0 auto', display: this.state.hideList ? 'none' : 'block' }} >
                {this.state.hideList==false? <ListFormComponent key='list_component' callback={(p) => {
                            if (p != null) {
                                let container = CircularJSON.parse(p.attachment) as ProgramContainer;
                                container.id = p.id;
                                container.createdById = p.createdById;// || container.createdBy;
                                container.Creator = p.userName;
                                container.version=p.version;
                                container.CategoryId=p.categoryId;
                                if(container.IsDirty){
                                    delete container.IsDirty;
                                }
                                this.strProgramContainer = CircularJSON.stringify(container);
                                //populateElementFindRoot(container);
                                this.setState({ hideWelcome: true, hideDetail: false, hideList: true, programContainer: container },
                                    () => window.scrollTo(0, 0))
                            } else {
                                let newProgramContaier = new ProgramContainer();
                                newProgramContaier.ListOfFormSections = [];
                                newProgramContaier.createdById = LocalStorageService.getUserId();
                                newProgramContaier.displayForm = 'hiddenStepper';
                                this.strProgramContainer = CircularJSON.stringify(newProgramContaier);
                                this.setState({ programContainer: newProgramContaier });
                            }
                            this.setState({ hideWelcome: true, hideDetail: false, hideList: true }, () => window.scrollTo(0, 0))
                    }} />: null}
                </div>

                <div style={{ maxWidth: '80%', margin: '0 auto', display: this.state.hideDetail ? 'none' : 'block' }} >
                    <DetailFormComponent isEng={true}
                        callback={p => {
                            this.setState({ programContainer: p }, () => {
                                // Check if form is dirty after state update
                                const isDirty = this.checkIfFormIsDirty();
                                if (isDirty && !this.state.isFormDirty) {
                                    this.markFormAsDirty();
                                }
                            });
                        }}
                        callbackOriginalData={(p) => {
                            
                            let container = CircularJSON.parse(this.strProgramContainer) as ProgramContainer;
                            this.setState({ programContainer: container})
                            console.log('container', container);
                        }}
                        callbackUpdateOriginalData={(p) => {
                            this.strProgramContainer = CircularJSON.stringify(p);
                        }}
                        hash={this.hash}
                        key={"DetailFormComponent"}
                        programContainer={this.state.programContainer} />
                </div>

                <FooterComponent state={this.state} />

            </div>
            </MsalProvider>
        )

    }

}
